// The signed-in frame: menu (side on desktop, bottom tabs on phones), the bell, and the current screen.
import { Suspense, useEffect, useRef, useState } from "react";
import { useIsFetching } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router";
import { setNavigate } from "../../lib/nav";
import { useSession } from "../../lib/session";
import { ErrorBoundary } from "../../ui/bits";
import { Icon, Logo } from "../../ui/Icon";
import { PageSkeleton } from "../../ui/loading";
import { useCounts } from "./counts";
import { BOTTOM, FILL, NAV, isItem, navOn, type NavItem } from "./nav";
import { AccountMenu } from "./AccountMenu";
import { AppRoutes } from "./routes";

export function Shell() {
  const { me, meta, modOn } = useSession();
  const loc = useLocation();
  setNavigate(useNavigate());
  const path = loc.pathname;
  const menu = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useCollapsed();
  // the current screen's menu entry stays in view (on a short window, Admin sits below the fold)
  useEffect(() => { menu.current?.querySelector("a.on")?.scrollIntoView({ block: "nearest" }); }, [path]);
  const counts = useCounts();
  // a screen is loading its first data: the old one stays up (dimmed if slow) instead of blanking
  const loading = useIsFetching({ predicate: (q) => q.state.data === undefined }) > 0;
  const cnt = (x: NavItem) => x.count && counts[x.count] ? <span className="count">{counts[x.count]}</span> : null;
  const visible = (x: NavItem) => x.roles.includes(me.role) && modOn(x.mod);
  const items = NAV.filter((x) => (isItem(x) ? visible(x) : x.roles.includes(me.role)));
  const byPath = (h: string) => NAV.find((x): x is NavItem => isItem(x) && x.h === h);
  const tabs = BOTTOM[me.role].map(byPath).filter((x): x is NavItem => !!x && modOn(x.mod));
  for (const h of FILL) { const x = byPath(h); if (tabs.length < 5 && x && x.roles.includes(me.role) && !tabs.includes(x)) tabs.push(x); }

  return (
    <>
      <div className={`shell ${collapsed ? "collapsed" : ""}`}>
        {/* brand pinned at the top, account at the bottom; only the menu between them scrolls. Collapsed: an icon rail */}
        <nav className="side" id="side" aria-label="Menu">
          <div className="side-top">
            <div className="brand"><Logo /><span className="nl">Compass</span></div>
            <button className="side-toggle" onClick={() => setCollapsed(!collapsed)} aria-expanded={!collapsed} aria-controls="side"
              title={`${collapsed ? "Expand" : "Collapse"} menu ( [ )`} aria-label={collapsed ? "Expand menu" : "Collapse menu"}><Icon name="sidebar" /></button>
          </div>
          <div className="side-scroll" ref={menu}>
            {items.map((x) => isItem(x) ? (
              <Link key={x.h} to={x.h} className={navOn(x.h, path) ? "on" : ""} title={collapsed ? x.l : undefined} aria-label={collapsed ? x.l : undefined}>
                <Icon name={x.ic} /><span className="nl">{x.l}</span>
                {x.mod && meta?.modules?.[x.mod] === false && <span className="offtag" title="Hidden from the team">off</span>}
                {cnt(x)}
              </Link>
            ) : <div key={x.sec} className="sec"><span className="nl">{x.sec}</span></div>)}
          </div>
          <AccountMenu unread={counts.inbox ?? 0} collapsed={collapsed} />
        </nav>
        {loading && <div className="toploader" aria-hidden="true" />}
        <main id="main" className={loading ? "busy" : ""}>
          <ErrorBoundary resetKey={path + loc.search}>
            <Suspense fallback={<PageSkeleton kind="list" />}><AppRoutes /></Suspense>
          </ErrorBoundary>
        </main>
      </div>
      <nav className="tabs-bottom" id="bottom">
        {tabs.map((x) => <Link key={x.h} to={x.h} className={navOn(x.h, path) ? "on" : ""}><Icon name={x.ic} /><span className="tl">{x.tab || x.l}</span>{cnt(x)}</Link>)}
      </nav>
      {me.role !== "ops" && <Link id="bell" className="bell" to="/inbox"><Icon name="bell" />{counts.inbox ? <span className="count">{counts.inbox}</span> : null}</Link>}
    </>
  );
}

const KEY = "compass.menu.collapsed";

/** Collapsed or not, remembered on this browser; "[" toggles it (not while typing). */
function useCollapsed(): [boolean, (v: boolean) => void] {
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem(KEY) === "1"; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem(KEY, collapsed ? "1" : "0"); } catch { /* private window: this visit only */ } }, [collapsed]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "[" || e.metaKey || e.ctrlKey || e.altKey || (e.target as HTMLElement).closest("input, textarea, select, [contenteditable]")) return;
      setCollapsed((c) => !c);
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, []);
  return [collapsed, setCollapsed];
}
